import React, { useState, useEffect, useLayoutEffect } from 'react';
import { 
  Layout, 
  Card, 
  Table, 
  Button, 
  Space, 
  Modal, 
  Form, 
  Select, 
  DatePicker, 
  Tag, 
  Typography, 
  Row, 
  Col, 
  message, 
  notification,
  Popconfirm,
  Tooltip,
  Divider,
  Upload,
  Input,
  Switch,
  Tabs,
  ConfigProvider
} from 'antd';
import ruRU from 'antd/locale/ru_RU';
import ContrastTag from '../components/ContrastTag';
import ColorTest from '../components/ColorTest';
import { 
  PlusOutlined, 
  SearchOutlined, 
  EditOutlined, 
  DeleteOutlined,
  SaveOutlined,
  HistoryOutlined,
  TeamOutlined,
  DownloadOutlined,
  UploadOutlined,
  FileExcelOutlined,
  PlusCircleOutlined,
  EyeOutlined,
  CopyOutlined,
  CloudUploadOutlined,
  BookOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import PhotoUpload from '../components/PhotoUpload';
import PhotoGallery from '../components/PhotoGallery';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import * as XLSX from 'xlsx';
import './ProductsNew.css';

// Устанавливаем русскую локаль для dayjs
dayjs.locale('ru');

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

const ProductsNew = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('АКТИВНО');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [hasOwner, setHasOwner] = useState(false);
  const [showColorTest, setShowColorTest] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  // Состояния для модальных окон истории и владельцев
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const [isS3ModalVisible, setIsS3ModalVisible] = useState(false);
  const [s3Files, setS3Files] = useState([]);
  const [s3Loading, setS3Loading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [imagePreviewVisible, setImagePreviewVisible] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [previewImageName, setPreviewImageName] = useState('');
  const [isOwnersModalVisible, setIsOwnersModalVisible] = useState(false);
  const [selectedProductForHistory, setSelectedProductForHistory] = useState(null);
  const [productHistory, setProductHistory] = useState([]);
  const [productOwners, setProductOwners] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [ownersLoading, setOwnersLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [showImportReport, setShowImportReport] = useState(false);
  const [importResults, setImportResults] = useState(null);

  // Состояния для модальных окон добавления справочников
  const [isAddSizeModalVisible, setIsAddSizeModalVisible] = useState(false);
  const [isAddColorModalVisible, setIsAddColorModalVisible] = useState(false);
  const [isAddMaterialModalVisible, setIsAddMaterialModalVisible] = useState(false);
  const [isAddProductionLocationModalVisible, setIsAddProductionLocationModalVisible] = useState(false);
  const [isAddReleaseLocationModalVisible, setIsAddReleaseLocationModalVisible] = useState(false);
  const [isReferenceModalVisible, setIsReferenceModalVisible] = useState(false);
  const [wasReferenceModalOpen, setWasReferenceModalOpen] = useState(false);
  
  const [addLoading, setAddLoading] = useState(false);
  const [newItemForm] = Form.useForm();
  const [selectedColor, setSelectedColor] = useState('#000000');
  const [forceUpdate, setForceUpdate] = useState(0);
  
  // Состояние для принудительного управления значениями Select
  const [formValues, setFormValues] = useState({});
  
  // Состояние для импорта
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvData, setCsvData] = useState([]);
  const [fullCsvData, setFullCsvData] = useState([]);
  const [fieldMapping, setFieldMapping] = useState({});
  
  // Состояние для фильтров
  const [productionFilter, setProductionFilter] = useState(null);
  const [releaseFilter, setReleaseFilter] = useState(null);
  const [dateRangeFilter, setDateRangeFilter] = useState(null);
  
  // Состояние для пагинации
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  
  // Фильтрация товаров
  const filteredProducts = products.filter(product => {
    if (productionFilter && product.productionLocation?.city !== productionFilter) {
      return false;
    }
    if (releaseFilter && product.releaseLocation?.city !== releaseFilter) {
      return false;
    }
    if (dateRangeFilter && product.releaseDate) {
      const productDate = new Date(product.releaseDate);
      const startDate = new Date(dateRangeFilter[0]);
      const endDate = new Date(dateRangeFilter[1]);
      
      // Устанавливаем время для корректного сравнения
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
      
      if (productDate < startDate || productDate > endDate) {
        return false;
      }
    }
    return true;
  });
  
  // Доступные поля системы для сопоставления
  const systemFields = [
    { key: 'название', label: 'Название товара', required: true },
    { key: 'серийный номер', label: 'Серийный номер', required: false },
    { key: 'идентификационный номер', label: 'Идентификационный номер', required: true },
    { key: 'размер', label: 'Размер', required: false },
    { key: 'цвет', label: 'Цвет', required: false },
    { key: 'материал', label: 'Материал', required: false },
    { key: 'место производства', label: 'Место производства', required: false },
    { key: 'место выпуска', label: 'Место выпуска', required: false },
    { key: 'дата выпуска', label: 'Дата выпуска', required: false },
    { key: 'дата сертификата', label: 'Дата сертификата', required: false },
    { key: 'url фото', label: 'URL фото', required: false },
    { key: 'особенности', label: 'Особенности', required: false },
    { key: 'статус', label: 'Статус', required: false },
    { key: 'описание', label: 'Описание', required: false }
  ];

  // Атрибуты товаров
  const [sizes, setSizes] = useState([]);
  const [colors, setColors] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [productionLocations, setProductionLocations] = useState([]);
  const [releaseLocations, setReleaseLocations] = useState([]);

  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    loadProductAttributes();
    loadProducts();
  }, [token, navigate]);

  // Перезагружаем товары при изменении фильтров
  useEffect(() => {
    if (token) {
      loadProducts();
    }
  }, [searchText, selectedStatus]);

  // Загружаем файлы S3 при открытии модального окна
  useEffect(() => {
    if (isS3ModalVisible) {
      loadS3Files();
    }
  }, [isS3ModalVisible]);

  // Эффект для установки значений формы при редактировании товара
  useEffect(() => {
    console.log('useEffect triggered:', {
      editingProduct: !!editingProduct,
      isModalVisible,
      sizesLength: sizes.length,
      colorsLength: colors.length,
      materialsLength: materials.length,
      productionLocationsLength: productionLocations.length,
      releaseLocationsLength: releaseLocations.length
    });
    
    if (editingProduct && isModalVisible && sizes.length > 0 && colors.length > 0 && materials.length > 0 && productionLocations.length > 0 && releaseLocations.length > 0) {
      console.log('useEffect: Устанавливаем значения формы для редактирования товара:', editingProduct.id);
      console.log('useEffect: Справочники загружены:', {
        sizes: sizes.length,
        colors: colors.length,
        materials: materials.length,
        productionLocations: productionLocations.length,
        releaseLocations: releaseLocations.length
      });
      
      const formData = {
        name: editingProduct.name,
        serialNumber: editingProduct.serialNumber,
        identificationNumber: editingProduct.identificationNumber,
        sizeId: editingProduct.size?.id,
        colorId: editingProduct.color?.id,
        materialId: editingProduct.material?.id,
        features: editingProduct.features,
        productionLocationId: editingProduct.productionLocation?.id,
        releaseLocationId: editingProduct.releaseLocation?.id,
        releaseDate: editingProduct.releaseDate ? dayjs(editingProduct.releaseDate) : null,
        certificateDate: editingProduct.certificateDate ? dayjs(editingProduct.certificateDate) : null,
        photoUrl: editingProduct.photoUrl,
        photos: editingProduct.photos || [], // Добавляем фотографии для редактирования
        description: editingProduct.description,
        status: editingProduct.status
      };
      
      console.log('useEffect: Данные для формы:', formData);
      form.setFieldsValue(formData);
    }
  }, [editingProduct, isModalVisible, sizes, colors, materials, productionLocations, releaseLocations, form]);

  // Дополнительный useLayoutEffect для синхронного обновления формы
  useLayoutEffect(() => {
    if (editingProduct && isModalVisible) {
      console.log('useLayoutEffect: Синхронная установка значений формы');
      
      const formData = {
        name: editingProduct.name,
        serialNumber: editingProduct.serialNumber,
        identificationNumber: editingProduct.identificationNumber,
        sizeId: editingProduct.size?.id,
        colorId: editingProduct.color?.id,
        materialId: editingProduct.material?.id,
        features: editingProduct.features,
        productionLocationId: editingProduct.productionLocation?.id,
        releaseLocationId: editingProduct.releaseLocation?.id,
        releaseDate: editingProduct.releaseDate ? dayjs(editingProduct.releaseDate) : null,
        certificateDate: editingProduct.certificateDate ? dayjs(editingProduct.certificateDate) : null,
        photoUrl: editingProduct.photoUrl,
        photos: editingProduct.photos || [], // Добавляем фотографии для редактирования
        description: editingProduct.description,
        status: editingProduct.status
      };
      
      form.setFieldsValue(formData);
    }
  }, [editingProduct, isModalVisible, form]);

  const loadProductAttributes = async () => {
    try {
      const [sizesRes, colorsRes, materialsRes, prodLocRes, relLocRes] = await Promise.all([
        axios.get('https://limitverify.ru:5000/api/product-attributes/sizes', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get('https://limitverify.ru:5000/api/product-attributes/colors', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get('https://limitverify.ru:5000/api/product-attributes/materials', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get('https://limitverify.ru:5000/api/product-attributes/production-locations', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get('https://limitverify.ru:5000/api/product-attributes/release-locations', {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      const sizesData = sizesRes.data.sizes || [];
      const colorsData = colorsRes.data.colors || [];
      const materialsData = materialsRes.data.materials || [];
      const productionLocationsData = prodLocRes.data.productionLocations || [];
      const releaseLocationsData = relLocRes.data.releaseLocations || [];
      
      console.log('Загружены справочники:', {
        sizes: sizesData.length,
        colors: colorsData.length,
        materials: materialsData.length,
        productionLocations: productionLocationsData.length,
        releaseLocations: releaseLocationsData.length
      });
      
      setSizes(sizesData);
      setColors(colorsData);
      setMaterials(materialsData);
      setProductionLocations(productionLocationsData);
      setReleaseLocations(releaseLocationsData);
    } catch (error) {
      console.error('Ошибка загрузки атрибутов:', error);
      message.error('Ошибка загрузки атрибутов товаров');
    }
  };

  // Загрузка истории товара
  const loadProductHistory = async (productId) => {
    setHistoryLoading(true);
    try {
      const response = await axios.get(`https://limitverify.ru:5000/api/product-history/product/${productId}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let history = response.data.data || [];
      
      // Сортируем по дате действия (новые сверху, старые снизу)
      history = history.sort((a, b) => {
        const dateA = new Date(a.actionDate || a.action_date || 0);
        const dateB = new Date(b.actionDate || b.action_date || 0);
        return dateB - dateA; // DESC - новые сверху
      });
      
      setProductHistory(history);
    } catch (error) {
      console.error('Ошибка загрузки истории:', error);
      message.error('Ошибка загрузки истории товара');
      setProductHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Загрузка владельцев товара
  const loadProductOwners = async (productId) => {
    setOwnersLoading(true);
    try {
      const response = await axios.get(`https://limitverify.ru:5000/api/product-history/product/${productId}/owners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Владельцы товара:', response.data);
      
      if (response.data.success && response.data.data) {
        setProductOwners(response.data.data);
      } else {
        console.log('Нет данных о владельцах или неверный формат ответа');
        setProductOwners([]);
      }
    } catch (error) {
      console.error('Ошибка загрузки владельцев:', error);
      message.error('Ошибка загрузки владельцев товара');
      setProductOwners([]);
    } finally {
      setOwnersLoading(false);
    }
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get('https://limitverify.ru:5000/api/products', {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          search: searchText,
          status: selectedStatus !== 'all' ? selectedStatus : '',
          page: 1,
          limit: 100,
          sortBy: 'createdAt',
          sortOrder: 'DESC'
        }
      });
      
      const products = response.data.products || [];
      console.log('Загружены товары:', products);
      if (products.length > 0) {
        console.log('Первый товар для примера:', products[0]);
        console.log('Поле public первого товара:', products[0].public);
        console.log('Связанные данные первого товара:', {
          size: products[0].size,
          color: products[0].color,
          material: products[0].material,
          productionLocation: products[0].productionLocation,
          releaseLocation: products[0].releaseLocation
        });
      }
      setProducts(products);
    } catch (error) {
      console.error('Ошибка загрузки товаров:', error);
      if (error.response?.status === 403) {
        message.error('У вас нет прав для просмотра товаров');
        navigate('/forbidden');
      } else {
        message.error('Ошибка загрузки товаров');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleStatusChange = (value) => {
    setSelectedStatus(value);
  };

  const showProductModal = (product = null) => {
    setEditingProduct(product);
    
    if (product) {
      console.log('Используем данные товара из таблицы:', product);
      console.log('Связанные данные товара:', {
        size: product.size,
        color: product.color,
        material: product.material,
        productionLocation: product.productionLocation,
        releaseLocation: product.releaseLocation
      });
      console.log('Фото товара:', product.photos);
      
      // Проверяем, есть ли у товара владелец
      const hasOwnerValue = product.clientId && product.clientId !== null;
      setHasOwner(hasOwnerValue);
      console.log('Товар имеет владельца:', hasOwnerValue, 'clientId:', product.clientId);
      
      // Проверяем, есть ли связанные данные
      const hasLinkedData = product.size || product.color || product.material || product.productionLocation || product.releaseLocation;
      console.log('Есть ли связанные данные:', hasLinkedData);
      
      if (!hasLinkedData) {
        console.warn('⚠️ ВНИМАНИЕ: У товара нет связанных данных справочников!');
        console.warn('Это означает, что бэкенд не возвращает связанные объекты в списке товаров.');
        
        // Показываем пользователю предупреждение
        message.warning('У товара отсутствуют данные справочников. Проверьте связь с базой данных.');
      } else {
        console.log('✅ Связанные данные найдены, устанавливаем в форму...');
      }
      
      const formData = {
        name: product.name,
        serialNumber: product.serialNumber,
        identificationNumber: product.identificationNumber,
        sizeId: product.size?.id,
        colorId: product.color?.id,
        materialId: product.material?.id,
        features: product.features,
        productionLocationId: product.productionLocation?.id,
        releaseLocationId: product.releaseLocation?.id,
        releaseDate: product.releaseDate ? dayjs(product.releaseDate) : null,
        certificateDate: product.certificateDate ? dayjs(product.certificateDate) : null,
        photoUrl: product.photoUrl,
        // Для нового формата с множественными фото
        photos: product.photos || (product.photoUrl ? [{
          id: 1,
          photoUrl: product.photoUrl,
          displayOrder: 1,
          isPrimary: true
        }] : []),
        description: product.description,
        status: product.status
      };
      
      console.log('Устанавливаем данные в форму:', formData);
      
      // Устанавливаем значения в состояние для принудительного управления
      setFormValues(formData);
      
      // Попробуем принудительно сбросить форму и установить значения заново
      form.resetFields();
      
      // Установка значений с небольшой задержкой после сброса
      setTimeout(() => {
        console.log('Установка значений после сброса формы');
        form.setFieldsValue(formData);
        
        // Принудительное обновление всех полей
        const fields = [
          'name', 'serialNumber', 'identificationNumber', 'sizeId', 'colorId', 
          'materialId', 'features', 'productionLocationId', 'releaseLocationId',
          'releaseDate', 'certificateDate', 'photoUrl', 'description', 'status'
        ];
        
        fields.forEach(field => {
          if (formData[field] !== undefined && formData[field] !== null) {
            form.setFieldValue(field, formData[field]);
          }
        });
        
        console.log('Значения полей установлены по отдельности');
        
        // Принудительное обновление компонента
        setForceUpdate(prev => prev + 1);
      }, 50);
    } else {
      form.resetFields();
      setFormValues({}); // Сбрасываем значения формы
      // Устанавливаем значения по умолчанию
      form.setFieldsValue({
        features: 'Отсутствие декоративных элементов',
        productionLocationId: productionLocations.find(loc => loc.isDefault)?.id,
        releaseLocationId: releaseLocations.find(loc => loc.isDefault)?.id,
        status: 'АКТИВНО'
      });
    }
    setIsModalVisible(true);
  };

  // Открытие модального окна истории товара
  const handleShowHistory = (product) => {
    setSelectedProductForHistory(product);
    setIsHistoryModalVisible(true);
    loadProductHistory(product.id);
  };

  // Открытие модального окна владельцев товара
  const handleShowOwners = (product) => {
    setSelectedProductForHistory(product);
    setIsOwnersModalVisible(true);
    loadProductOwners(product.id);
  };

  // Переключение публичного статуса товара
  const handleTogglePublic = async (productId, newPublicStatus) => {
    try {
      // Обновляем состояние загрузки для конкретного товара
      setProducts(prevProducts => 
        prevProducts.map(product => 
          product.id === productId 
            ? { ...product, updatingPublic: true }
            : product
        )
      );

      const response = await axios.put(
        `https://limitverify.ru:5000/api/products/${productId}/public`,
        { public: newPublicStatus },
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        // Обновляем товар в списке
        setProducts(prevProducts => 
          prevProducts.map(product => 
            product.id === productId 
              ? { ...product, public: newPublicStatus, updatingPublic: false }
              : product
          )
        );
        
        message.success(`Товар ${newPublicStatus ? 'добавлен в' : 'удален из'} публичный каталог`);
      }
    } catch (error) {
      console.error('Error toggling public status:', error);
      message.error('Ошибка при изменении статуса товара');
      
      // Сбрасываем состояние загрузки при ошибке
      setProducts(prevProducts => 
        prevProducts.map(product => 
          product.id === productId 
            ? { ...product, updatingPublic: false }
            : product
        )
      );
    }
  };

  // Экспорт в Excel с правильной шириной колонок
  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      const response = await axios.get('https://limitverify.ru:5000/api/products/export/data', {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          search: searchText,
          status: selectedStatus !== 'all' ? selectedStatus : '',
          limit: 10000 // Большой лимит для экспорта
        }
      });

      const products = response.data.products || [];
      
      // Заголовки с новыми полями
      const headers = [
        'ID',
        'Название',
        'Серийный номер',
        'Идентификационный номер',
        'Размер',
        'Цвет',
        'Материал',
        'Особенности',
        'Место производства',
        'Место выпуска',
        'Дата выпуска',
        'Дата сертификата',
        'URL фото',
        'Описание',
        'Статус',
        'Дата создания',
        'Дата обновления',
        'Владельцы',
        'История'
      ];

      // Подготавливаем данные для Excel
      const excelData = products.map(product => [
        product.id,
        product.name || '',
        product.serialNumber || '',
        product.identificationNumber || '',
        product.size || '',
        product.color || '',
        product.material || '',
        product.features || '',
        product.productionLocation || '',
        product.releaseLocation || '',
        product.releaseDate || '',
        product.certificateDate || '',
        product.photoUrl || '',
        product.description || '',
        product.status || '',
        product.createdAt || '',
        product.updatedAt || '',
        product.owners || '',
        product.history || ''
      ]);

      // Создаем рабочую книгу Excel
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([headers, ...excelData]);

      // Устанавливаем ширину колонок
      const colWidths = [
        { wch: 8 },   // ID
        { wch: 25 },  // Название
        { wch: 15 },  // Серийный номер
        { wch: 20 },  // Идентификационный номер
        { wch: 12 },  // Размер
        { wch: 12 },  // Цвет
        { wch: 15 },  // Материал
        { wch: 30 },  // Особенности
        { wch: 25 },  // Место производства
        { wch: 25 },  // Место выпуска
        { wch: 12 },  // Дата выпуска
        { wch: 15 },  // Дата сертификата
        { wch: 30 },  // URL фото
        { wch: 30 },  // Описание
        { wch: 12 },  // Статус
        { wch: 18 },  // Дата создания
        { wch: 18 },  // Дата обновления
        { wch: 20 },  // Владельцы
        { wch: 80 }   // История - делаем очень широкой
      ];
      
      ws['!cols'] = colWidths;

      // Добавляем лист в книгу
      XLSX.utils.book_append_sheet(wb, ws, 'Товары');

      // Генерируем и скачиваем файл
      const fileName = `products_with_history_${dayjs().format('YYYY-MM-DD_HH-mm')}.xlsx`;
      XLSX.writeFile(wb, fileName);

      message.success(`Данные успешно экспортированы в Excel (${products.length} товаров)`);
    } catch (error) {
      console.error('Ошибка экспорта Excel:', error);
      message.error('Ошибка экспорта данных');
    } finally {
      setExportLoading(false);
    }
  };


  // Экспорт в CSV (оригинальная функция)
  const handleExportCSV = async () => {
    setExportLoading(true);
    try {
      const response = await axios.get('https://limitverify.ru:5000/api/products/export/data', {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          search: searchText,
          status: selectedStatus !== 'all' ? selectedStatus : '',
          limit: 10000
        }
      });

      const products = response.data.products || [];
      
      // Заголовки CSV с новыми полями
      const headers = [
        'ID',
        'Название',
        'Серийный номер',
        'Идентификационный номер',
        'Размер',
        'Цвет',
        'Материал',
        'Особенности',
        'Место производства',
        'Место выпуска',
        'Дата выпуска',
        'Дата сертификата',
        'URL фото',
        'Описание',
        'Статус',
        'Дата создания',
        'Дата обновления',
        'Владельцы',
        'История                    ' // Добавляем пробелы для расширения колонки
      ];

      // Конвертируем данные в CSV формат
      const csvContent = [
        headers.join(';'),
        ...products.map(product => {
          // Обрабатываем историю: сортируем по дате (самая ранняя внизу)
          let sortedHistory = '';
          if (product.history) {
            const historyEntries = product.history.split('\n').filter(entry => entry.trim() !== '');
            
            // Парсим и сортируем по дате
            historyEntries.sort((a, b) => {
              const dateA = dayjs(a.substring(0, 19), 'DD.MM.YYYY HH:mm:ss');
              const dateB = dayjs(b.substring(0, 19), 'DD.MM.YYYY HH:mm:ss');
              return dateA.valueOf() - dateB.valueOf(); // Сортировка по возрастанию даты
            });
            sortedHistory = historyEntries.join('\n');
          }

          return [
            product.id,
            `"${product.name || ''}"`,
            `"${product.serialNumber || ''}"`,
            `"${product.identificationNumber || ''}"`,
            `"${product.size || ''}"`,
            `"${product.color || ''}"`,
            `"${product.material || ''}"`,
            `"${product.features || ''}"`,
            `"${product.productionLocation || ''}"`,
            `"${product.releaseLocation || ''}"`,
            product.releaseDate || '',
            product.certificateDate || '',
            `"${product.photoUrl || ''}"`,
            `"${product.description || ''}"`,
            `"${product.status || ''}"`,
            product.createdAt || '',
            product.updatedAt || '',
            `"${product.owners || ''}"`,
            // Добавляем пробелы для расширения колонки истории
            `"${(sortedHistory || '').replace(/"/g, '""')}                    "`
          ].join(';');
        })
      ].join('\n');

      // Создаем и скачиваем файл
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `products_with_history_${dayjs().format('YYYY-MM-DD_HH-mm')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success(`Данные успешно экспортированы в CSV с разделителем ";" (${products.length} товаров)`);
    } catch (error) {
      console.error('Ошибка экспорта CSV:', error);
      message.error('Ошибка экспорта данных');
    } finally {
      setExportLoading(false);
    }
  };

  // Импорт товаров - первый этап: парсинг файла
  const handleImportProducts = (file) => {
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const text = e.target.result;
          const lines = text.split('\n');
          const headers = lines[0].split(';').map(h => h.replace(/"/g, '').trim());
          
          // Парсим данные (первые 5 строк для предпросмотра)
          const previewData = [];
          const allData = [];
          
          for (let i = 1; i < lines.length; i++) {
            if (lines[i].trim()) {
              const values = lines[i].split(';').map(v => v.replace(/"/g, '').trim());
              const row = {};
              headers.forEach((header, index) => {
                row[header] = values[index] || '';
              });
              
              // Добавляем во все данные
              allData.push(row);
              
              // Добавляем в предпросмотр (первые 5 строк)
              if (previewData.length < 5) {
                previewData.push(row);
              }
            }
          }
          
          // Сохраняем данные для модального окна
          setCsvHeaders(headers);
          setCsvData(previewData);
          setFullCsvData(allData);
          
          // Автоматическое сопоставление полей по названию
          const autoMapping = {};
          headers.filter(header => 
            !['id', 'ID', 'Id'].includes(header.trim())
          ).forEach(header => {
            const lowerHeader = header.toLowerCase().trim();
            const matchedField = systemFields.find(field => 
              field.key.toLowerCase() === lowerHeader || 
              field.label.toLowerCase().includes(lowerHeader) ||
              lowerHeader.includes(field.key.toLowerCase())
            );
            if (matchedField) {
              autoMapping[header] = matchedField.key;
            }
          });
          
          setFieldMapping(autoMapping);
          setIsImportModalVisible(true);
          
        } catch (error) {
          console.error('Ошибка обработки файла:', error);
          message.error('Ошибка обработки файла');
        }
      };
      
      reader.readAsText(file);
    } catch (error) {
      console.error('Ошибка импорта:', error);
      message.error('Ошибка импорта файла');
    }
    return false; // Предотвращаем автоматическую загрузку
  };

  // Загрузка списка файлов из приватного S3 (папка zagruzka)
  const loadS3Files = async () => {
    setS3Loading(true);
    try {
      const response = await axios.get('https://limitverify.ru:5000/api/s3-zagruzka/files', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setS3Files(response.data.files || []);
    } catch (error) {
      console.error('Ошибка загрузки файлов из S3:', error);
      message.error('Ошибка загрузки файлов из S3');
    } finally {
      setS3Loading(false);
    }
  };

  // Копирование ссылки на файл
  const copyFileLink = async (fileUrl) => {
    try {
      // Убираем подписанные параметры AWS для простой ссылки
      const simpleUrl = fileUrl.split('?')[0];
      await navigator.clipboard.writeText(simpleUrl);
      message.success('Ссылка скопирована в буфер обмена');
    } catch (error) {
      console.error('Ошибка копирования:', error);
      message.error('Ошибка копирования ссылки');
    }
  };

  // Удаление файла из S3 (zagruzka)
  const deleteS3File = async (fileName) => {
    try {
      await axios.delete(`https://limitverify.ru:5000/api/s3-zagruzka/files/${encodeURIComponent(fileName)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      message.success('Файл успешно удален');
      loadS3Files(); // Перезагружаем список файлов
    } catch (error) {
      console.error('Ошибка удаления файла:', error);
      message.error('Ошибка удаления файла');
    }
  };

  // Показать изображение в модальном окне
  const showImagePreview = (imageUrl, imageName) => {
    setPreviewImageUrl(imageUrl);
    setPreviewImageName(imageName);
    setImagePreviewVisible(true);
  };

  // Пакетная загрузка файлов
  const handleBulkUpload = async (files) => {
    setS3Loading(true);
    try {
      // Получаем список существующих файлов
      const existingFilesResponse = await axios.get('https://limitverify.ru:5000/api/s3-zagruzka/files', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const existingFiles = existingFilesResponse.data.files || [];
      const existingFileNames = existingFiles.map(file => file.name);

      const formData = new FormData();
      let filesToUpload = 0;

      files.forEach((file, index) => {
        // Создаем имя файла с префиксом load_ (если его еще нет)
        const newFileName = file.name.startsWith('load_') 
          ? file.name 
          : `load_${file.name}`;
        
        // Проверяем, не существует ли уже файл с таким именем
        if (!existingFileNames.includes(newFileName)) {
          formData.append('files', file, newFileName);
          filesToUpload++;
        } else {
          console.log(`Файл ${newFileName} уже существует, пропускаем`);
        }
      });

      if (filesToUpload === 0) {
        message.warning('Все файлы уже существуют в бакете');
        return;
      }

      const response = await axios.post('https://limitverify.ru:5000/api/s3-zagruzka/upload-bulk', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      message.success(`Успешно загружено ${filesToUpload} новых файлов`);
      loadS3Files(); // Перезагружаем список файлов
    } catch (error) {
      console.error('Ошибка пакетной загрузки:', error);
      message.error('Ошибка загрузки файлов');
    } finally {
      setS3Loading(false);
    }
  };

  // Финальный импорт товаров
  const handleFinalImport = async () => {
    setImportLoading(true);
    try {
      // Преобразуем данные согласно сопоставлению полей
      const products = fullCsvData.map(row => {
        const product = {};
        Object.keys(fieldMapping).forEach(csvHeader => {
          const systemField = fieldMapping[csvHeader];
          if (row[csvHeader]) {
            product[systemField] = row[csvHeader];
          }
        });
        return product;
      }).filter(product => product['название'] && product['идентификационный номер']);

      console.log('Данные для импорта:', products);
      console.log('Количество товаров для импорта:', products.length);
      console.log('Полные данные CSV:', fullCsvData);
      console.log('Сопоставление полей:', fieldMapping);

      if (products.length === 0) {
        message.error('Нет товаров для импорта. Проверьте сопоставление полей.');
        return;
      }

      // Отправляем данные на сервер для импорта
      const response = await axios.post('https://limitverify.ru:5000/api/products/import', {
        products: products
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Ответ сервера:', response.data);
      console.log('Детали результатов:', response.data.results);
      console.log('Проверяем наличие details:', response.data.results?.details);
      console.log('Длина details:', response.data.results?.details?.length);
      console.log('Тип details:', typeof response.data.results?.details);
      console.log('Is Array:', Array.isArray(response.data.results?.details));

      // Убираем дублирующийся простой отчет

      // Показываем детальный отчет - упрощаем условие
      console.log('Начинаем проверку условий для отчета');
      console.log('response.data.results существует?', !!response.data.results);
      console.log('response.data.results.details существует?', !!response.data.results?.details);
      console.log('response.data.results.details.length > 0?', response.data.results?.details?.length > 0);
      
      // Сохраняем результаты и показываем отчет в том же модальном окне
      setImportResults(response.data.results);
      setShowImportReport(true);
      
      if (response.data.results.success > 0) {
        message.success(`Успешно импортировано ${response.data.results.success} товаров`);
        loadProducts(); // Перезагружаем список
      }
      
      if (response.data.results.errors > 0) {
        console.log('Детали ошибок:', response.data.results.details);
        message.error(`Ошибок импорта: ${response.data.results.errors}. Проверьте консоль для деталей.`);
      }

      // Убираем тестовый отчет, чтобы не мешал основному
      
    } catch (error) {
      console.error('Ошибка импорта:', error);
      message.error('Ошибка импорта товаров');
    } finally {
      setImportLoading(false);
    }
  };

  // Функции для добавления элементов в справочники
  const handleAddSize = async (values) => {
    setAddLoading(true);
    const wasOpen = wasReferenceModalOpen;
    try {
      const response = await axios.post('https://limitverify.ru:5000/api/product-attributes/sizes', {
        name: values.name,
        description: values.description || ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      message.success('Размер добавлен успешно');
      setIsAddSizeModalVisible(false);
      newItemForm.resetFields();
      
      // Перезагружаем справочники
      await loadProductAttributes();
      
      // Автоматически выбираем только что добавленный размер
      if (response.data && response.data.data) {
        form.setFieldsValue({ sizeId: response.data.data.id });
      }
      
      // Если модальное окно справочников было открыто, открываем его снова
      if (wasOpen) {
        setIsReferenceModalVisible(true);
        setWasReferenceModalOpen(false);
      }
    } catch (error) {
      console.error('Ошибка добавления размера:', error);
      message.error('Ошибка добавления размера');
    } finally {
      setAddLoading(false);
    }
  };

  const handleAddColor = async (values) => {
    setAddLoading(true);
    const wasOpen = wasReferenceModalOpen;
    try {
      const response = await axios.post('https://limitverify.ru:5000/api/product-attributes/colors', {
        name: values.name,
        hexCode: values.hexCode || '#000000'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      message.success('Цвет добавлен успешно');
      setIsAddColorModalVisible(false);
      newItemForm.resetFields();
      setSelectedColor('#000000');
      
      // Перезагружаем справочники
      await loadProductAttributes();
      
      // Автоматически выбираем только что добавленный цвет
      if (response.data && response.data.data) {
        form.setFieldsValue({ colorId: response.data.data.id });
      }
      
      // Если модальное окно справочников было открыто, открываем его снова
      if (wasOpen) {
        setIsReferenceModalVisible(true);
        setWasReferenceModalOpen(false);
      }
    } catch (error) {
      console.error('Ошибка добавления цвета:', error);
      message.error('Ошибка добавления цвета');
    } finally {
      setAddLoading(false);
    }
  };

  const handleAddMaterial = async (values) => {
    setAddLoading(true);
    const wasOpen = wasReferenceModalOpen;
    try {
      const response = await axios.post('https://limitverify.ru:5000/api/product-attributes/materials', {
        name: values.name,
        description: values.description || ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      message.success('Материал добавлен успешно');
      setIsAddMaterialModalVisible(false);
      newItemForm.resetFields();
      
      // Перезагружаем справочники
      await loadProductAttributes();
      
      // Автоматически выбираем только что добавленный материал
      if (response.data && response.data.data) {
        form.setFieldsValue({ materialId: response.data.data.id });
      }
      
      // Если модальное окно справочников было открыто, открываем его снова
      if (wasOpen) {
        setIsReferenceModalVisible(true);
        setWasReferenceModalOpen(false);
      }
    } catch (error) {
      console.error('Ошибка добавления материала:', error);
      message.error('Ошибка добавления материала');
    } finally {
      setAddLoading(false);
    }
  };

  const handleAddProductionLocation = async (values) => {
    setAddLoading(true);
    const wasOpen = wasReferenceModalOpen;
    try {
      const response = await axios.post('https://limitverify.ru:5000/api/product-attributes/production-locations', {
        country: values.country,
        region: values.region,
        city: values.city,
        fullAddress: values.fullAddress
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      message.success('Место производства добавлено успешно');
      setIsAddProductionLocationModalVisible(false);
      newItemForm.resetFields();
      
      // Перезагружаем справочники
      await loadProductAttributes();
      
      // Автоматически выбираем только что добавленное место производства
      const createdId = response.data?.id || response.data?.data?.id;
      if (createdId) {
        form.setFieldsValue({ productionLocationId: createdId });
      }
      
      // Если модальное окно справочников было открыто, открываем его снова
      if (wasOpen) {
        setIsReferenceModalVisible(true);
        setWasReferenceModalOpen(false);
      }
    } catch (error) {
      console.error('Ошибка добавления места производства:', error);
      const apiError = error.response?.data?.error;
      message.error(
        apiError === 'Country and city are required'
          ? 'Укажите страну и город'
          : (apiError || 'Ошибка добавления места производства')
      );
    } finally {
      setAddLoading(false);
    }
  };

  const handleAddReleaseLocation = async (values) => {
    setAddLoading(true);
    const wasOpen = wasReferenceModalOpen;
    try {
      const response = await axios.post('https://limitverify.ru:5000/api/product-attributes/release-locations', {
        country: values.country,
        region: values.region,
        city: values.city,
        fullAddress: values.fullAddress
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      message.success('Место выпуска добавлено успешно');
      setIsAddReleaseLocationModalVisible(false);
      newItemForm.resetFields();
      
      // Перезагружаем справочники
      await loadProductAttributes();
      
      // Автоматически выбираем только что добавленное место выпуска
      const createdId = response.data?.id || response.data?.data?.id;
      if (createdId) {
        form.setFieldsValue({ releaseLocationId: createdId });
      }
      
      // Если модальное окно справочников было открыто, открываем его снова
      if (wasOpen) {
        setIsReferenceModalVisible(true);
        setWasReferenceModalOpen(false);
      }
    } catch (error) {
      console.error('Ошибка добавления места выпуска:', error);
      const apiError = error.response?.data?.error;
      message.error(
        apiError === 'Country and city are required'
          ? 'Укажите страну и город'
          : (apiError || 'Ошибка добавления места выпуска')
      );
    } finally {
      setAddLoading(false);
    }
  };

  // Обработчики удаления элементов справочников
  const handleDeleteSize = async (id) => {
    try {
      await axios.delete(`https://limitverify.ru:5000/api/product-attributes/sizes/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      message.success('Размер удален успешно');
      await loadProductAttributes();
    } catch (error) {
      console.error('Ошибка удаления размера:', error);
      message.error('Ошибка удаления размера');
    }
  };

  const handleDeleteColor = async (id) => {
    try {
      await axios.delete(`https://limitverify.ru:5000/api/product-attributes/colors/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      message.success('Цвет удален успешно');
      await loadProductAttributes();
    } catch (error) {
      console.error('Ошибка удаления цвета:', error);
      message.error('Ошибка удаления цвета');
    }
  };

  const handleDeleteMaterial = async (id) => {
    try {
      await axios.delete(`https://limitverify.ru:5000/api/product-attributes/materials/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      message.success('Материал удален успешно');
      await loadProductAttributes();
    } catch (error) {
      console.error('Ошибка удаления материала:', error);
      message.error('Ошибка удаления материала');
    }
  };

  const handleDeleteProductionLocation = async (id) => {
    try {
      await axios.delete(`https://limitverify.ru:5000/api/product-attributes/production-locations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      message.success('Место производства удалено успешно');
      await loadProductAttributes();
    } catch (error) {
      console.error('Ошибка удаления места производства:', error);
      message.error('Ошибка удаления места производства');
    }
  };

  const handleDeleteReleaseLocation = async (id) => {
    try {
      await axios.delete(`https://limitverify.ru:5000/api/product-attributes/release-locations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      message.success('Место выпуска удалено успешно');
      await loadProductAttributes();
    } catch (error) {
      console.error('Ошибка удаления места выпуска:', error);
      message.error('Ошибка удаления места выпуска');
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      console.log('Начинаем сохранение товара...');
      
      const values = await form.validateFields();
      console.log('Данные формы:', values);
      
      const productData = {
        ...values,
        releaseDate: values.releaseDate ? values.releaseDate.format('YYYY-MM-DD') : null,
        certificateDate: values.certificateDate ? values.certificateDate.format('YYYY-MM-DD') : null,
        status: values.status || 'АКТИВНО'
      };

      // Обрабатываем фото - если есть множественные фото, используем их, иначе берем первое фото
      if (values.photos && values.photos.length > 0) {
        // Новый формат - множественные фото
        productData.photos = values.photos;
        // Удаляем старое поле photoUrl
        delete productData.photoUrl;
      } else if (values.photoUrl) {
        // Старый формат - одно фото (для обратной совместимости)
        delete productData.photos;
      } else {
        // Нет фото - это ошибка
        throw new Error('Необходимо загрузить хотя бы одно фото');
      }
      
      console.log('Данные для отправки:', productData);
      console.log('Токен авторизации:', token ? 'Есть' : 'Отсутствует');

      if (editingProduct) {
        // Обновление существующего товара
        console.log('Обновляем существующий товар:', editingProduct.id);
        const response = await axios.put(`https://limitverify.ru:5000/api/products/${editingProduct.id}`, productData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Ответ сервера:', response.data);
        
        message.success('Изделие обновлено успешно!');
        setIsModalVisible(false);
        setEditingProduct(null);
        loadProducts(); // Перезагружаем список
      } else {
        // Создание нового товара
        console.log('Создаем новый товар...');
        const response = await axios.post('https://limitverify.ru:5000/api/products', productData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Ответ сервера:', response.data);
        
        message.success('Изделие создано успешно!');
        setIsModalVisible(false);
        setEditingProduct(null);
        loadProducts(); // Перезагружаем список
      }
    } catch (error) {
      console.error('Ошибка сохранения:', error);
      console.error('Детали ошибки:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      
      if (error.response?.status === 403) {
        console.log('Показываем ошибку 403');
        message.error('У вас нет прав для изменения товаров');
      } else if (error.response?.status === 400) {
        console.log('Показываем ошибку 400:', error.response?.data?.error);
        // Показываем ошибку пользователю
        alert(error.response?.data?.error || 'Ошибка валидации данных');
      } else if (error.response?.status === 401) {
        console.log('Показываем ошибку 401');
        message.error('Ошибка авторизации. Войдите в систему заново.');
      } else {
        console.log('Показываем общую ошибку:', error.response?.data?.error || error.message);
        message.error('Ошибка при сохранении изделия: ' + (error.response?.data?.error || error.message));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (productId) => {
    try {
      await axios.delete(`https://limitverify.ru:5000/api/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      message.success('Изделие удалено успешно!');
      loadProducts(); // Перезагружаем список
    } catch (error) {
      console.error('Ошибка удаления:', error);
      if (error.response?.status === 403) {
        message.error('У вас нет прав для удаления товаров');
      } else {
        message.error('Ошибка удаления изделия');
      }
    }
  };

  const columns = [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500, fontSize: '13px' }}>{text}</div>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {record.serialNumber} / {record.identificationNumber}
          </Text>
        </div>
      )
    },
    {
      title: 'Атрибуты',
      key: 'attributes',
      width: 140,
      responsive: ['md'],
      render: (_, record) => (
        <div style={{ lineHeight: '1.2' }}>
          {record.size && <Tag color="blue" style={{ fontSize: '10px', margin: '1px' }}>{record.size.name}</Tag>}
          {record.color && (
            <ContrastTag backgroundColor={record.color.hexCode}>
              {record.color.name}
            </ContrastTag>
          )}
          {record.material && <Tag color="green" style={{ fontSize: '10px', margin: '1px' }}>{record.material.name}</Tag>}
        </div>
      )
    },
    {
      title: 'Производство',
      key: 'production',
      width: 120,
      responsive: ['sm'],
      render: (_, record) => (
        <div style={{ fontSize: '11px', lineHeight: '1.3' }}>
          <div style={{ marginBottom: '4px' }}>
            <Text type="secondary" style={{ fontWeight: '500', color: '#1890ff', fontSize: '10px' }}>
              Производство:
            </Text>
          <Text type="secondary">
            {record.productionLocation?.city || 'Не указано'}
          </Text>
          </div>
          <div>
            <Text type="secondary" style={{ fontWeight: '500', color: '#1890ff', fontSize: '10px' }}>
              Выпуск:
            </Text>
          <Text type="secondary">
            {record.releaseLocation?.city || 'Не указано'}
          </Text>
          </div>
        </div>
      )
    },
    {
      title: 'Даты',
      key: 'dates',
      width: 80,
      responsive: ['md'],
      render: (_, record) => (
        <div style={{ fontSize: '11px', lineHeight: '1.3' }}>
          <div style={{ marginBottom: '4px' }}>
            <Text type="secondary" style={{ fontWeight: '500', color: '#1890ff', fontSize: '10px' }}>
              Создан:
            </Text>
          <Text type="secondary">
            {record.releaseDate ? new Date(record.releaseDate).toLocaleDateString('ru-RU') : 'Не указано'}
          </Text>
          </div>
          <div>
            <Text type="secondary" style={{ fontWeight: '500', color: '#1890ff', fontSize: '10px' }}>
              Сертификат:
            </Text>
          <Text type="secondary">
            {record.certificateDate ? new Date(record.certificateDate).toLocaleDateString('ru-RU') : 'Не указано'}
          </Text>
          </div>
        </div>
      )
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 70,
      render: (status) => {
        let color = 'default';
        
        if (status === 'АКТИВНО') {
          color = 'green';
        } else if (status === 'АННУЛИРОВАНО') {
          color = 'red';
        } else if (status === 'АРХИВ') {
          color = 'orange';
        }
        
        return (
          <Tag color={color} style={{ fontSize: '10px' }}>
            {status}
          </Tag>
        );
      }
    },
    {
      title: 'Публичный',
      dataIndex: 'public',
      key: 'public',
      width: 80,
      align: 'center',
      render: (isPublic, record) => (
        <Switch
          checked={isPublic}
          onChange={(checked) => handleTogglePublic(record.id, checked)}
          loading={record.updatingPublic}
          size="small"
        />
      )
    },
    {
      title: 'Владелец',
      key: 'owner',
      width: 100,
      align: 'center',
      responsive: ['lg'],
      render: (_, record) => {
        if (record.clientId && record.client) {
          const clientName = record.client.fullName || 
            `${record.client.firstName || ''} ${record.client.lastName || ''}`.trim() || 
            record.client.email || 
            'Неизвестный';
          return (
            <div style={{ textAlign: 'center' }}>
              <Tooltip title={`Email: ${record.client.email || 'не указан'}`}>
                <Tag color="blue" style={{ fontSize: '10px' }}>
                  <TeamOutlined style={{ marginRight: '4px' }} />
                  {clientName}
                </Tag>
              </Tooltip>
            </div>
          );
        } else if (record.clientId) {
          // Если есть clientId, но нет данных клиента
          return (
            <div style={{ textAlign: 'center' }}>
              <Tag color="blue" style={{ fontSize: '10px' }}>
                <TeamOutlined style={{ marginRight: '4px' }} />
                Есть владелец
              </Tag>
            </div>
          );
        } else {
          return (
            <div style={{ textAlign: 'center' }}>
              <Tag color="default" style={{ fontSize: '10px' }}>
                Свободно
              </Tag>
            </div>
          );
        }
      }
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 120,
      fixed: 'right', // Фиксируем колонку справа
      responsive: false, // Всегда показывать колонку
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Редактировать">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              onClick={() => showProductModal(record)}
              size="small"
              style={{ padding: '2px 4px' }}
            />
          </Tooltip>
          <Tooltip title="История товара">
            <Button 
              type="text" 
              icon={<HistoryOutlined />} 
              onClick={() => handleShowHistory(record)}
              size="small"
              style={{ padding: '2px 4px' }}
            />
          </Tooltip>
          <Tooltip title="Владельцы товара">
            <Button 
              type="text" 
              icon={<TeamOutlined />} 
              onClick={() => handleShowOwners(record)}
              size="small"
              style={{ padding: '2px 4px' }}
            />
          </Tooltip>
          <Popconfirm
            title="Удалить изделие?"
            description="Это действие нельзя отменить"
            onConfirm={() => handleDelete(record.id)}
            okText="Да"
            cancelText="Нет"
          >
            <Tooltip title="Удалить">
              <Button 
                type="text" 
                danger 
                icon={<DeleteOutlined />} 
                size="small"
                style={{ padding: '2px 4px' }}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <ConfigProvider locale={ruRU}>
      <Layout style={{ minHeight: '100vh', maxWidth: '100vw' }}>
        <Navigation />
        
        <Content style={{ 
          padding: '16px', 
          background: '#f0f2f5',
          minHeight: 'calc(100vh - 64px)'
        }}>
        <div style={{ width: '100%', margin: '0', display: 'flex', flexDirection: 'column' }}>
          {/* Заголовок */}
          <div style={{ marginBottom: '16px', flexShrink: 0 }}>
            <div style={{ marginBottom: '12px' }}>
              <Title level={3} style={{ margin: 0 }}>Изделия</Title>
              <Text type="secondary" style={{ fontSize: '12px' }}>Управление каталогом изделий</Text>
            </div>
            <Row gutter={[8, 8]} justify="start" align="middle">
              <Col xs={12} sm={6} md={3}>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={() => showProductModal()}
                  style={{ width: '100%', marginBottom: '8px' }}
            >
              Добавить
            </Button>
              </Col>
              <Col xs={12} sm={6} md={3}>
                <Button 
                  icon={<DownloadOutlined />}
                  onClick={handleExportCSV}
                  loading={exportLoading}
                  style={{ width: '100%', marginBottom: '8px' }}
                >
                  CSV
                </Button>
              </Col>
              <Col xs={12} sm={6} md={3}>
                <Button 
                  icon={<FileExcelOutlined />}
                  onClick={handleExportExcel}
                  loading={exportLoading}
                  style={{ width: '100%', marginBottom: '8px' }}
                >
                  Excel
                </Button>
              </Col>
              <Col xs={12} sm={6} md={3}>
                <Upload
                  accept=".csv"
                  showUploadList={false}
                  beforeUpload={handleImportProducts}
                  disabled={importLoading}
                  style={{ width: '100%' }}
                >
                  <Button 
                    icon={<UploadOutlined />}
                    loading={importLoading}
                    style={{ width: '100%', marginBottom: '8px' }}
                  >
                    Загрузить
                  </Button>
                </Upload>
              </Col>
              <Col xs={12} sm={6} md={3}>
                <Button 
                  icon={<UploadOutlined />}
                  onClick={() => setIsS3ModalVisible(true)}
                  style={{ width: '100%', marginBottom: '8px' }}
                >
                  Загрузка фото
                </Button>
              </Col>
              <Col xs={12} sm={6} md={3}>
                <Button 
                  icon={<BookOutlined />}
                  onClick={() => setIsReferenceModalVisible(true)}
                  style={{ width: '100%', marginBottom: '8px' }}
                >
                  Справочники
                </Button>
              </Col>
              <Col xs={12} sm={6} md={3}>
                <Button 
                  type="default" 
                  size="small"
                  onClick={() => setShowColorTest(!showColorTest)}
                  style={{ width: '100%', marginBottom: '8px' }}
                >
                  {showColorTest ? 'Скрыть тест' : 'Тест цветов'}
                </Button>
              </Col>
            </Row>
          </div>

          {/* Основные фильтры */}
          <Card style={{ marginBottom: '16px', flexShrink: 0 }} bodyStyle={{ padding: '12px' }}>
            <Row gutter={[12, 8]} align="middle">
              <Col xs={24} sm={16} md={8}>
                <Input
                  placeholder="Поиск по названию, серийному номеру"
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => handleSearch(e.target.value)}
                  allowClear
                  size="small"
                />
              </Col>
              <Col xs={12} sm={4} md={4}>
                <Select
                  placeholder="Статус"
                  value={selectedStatus}
                  onChange={handleStatusChange}
                  style={{ width: '100%' }}
                  size="small"
                >
                  <Option value="all">Все</Option>
                  <Option value="АКТИВНО">Активные</Option>
                  <Option value="АННУЛИРОВАНО">Аннулированные</Option>
                  <Option value="АРХИВ">Архив</Option>
                </Select>
              </Col>
              <Col xs={12} sm={4} md={4}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Найдено: {filteredProducts.length}
                </Text>
              </Col>
            </Row>
            
            {/* Кнопка для показа дополнительных фильтров на мобильных */}
            <div className="mobile-filter-toggle">
              <Button 
                type="link" 
                size="small"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                style={{ padding: '4px 0', marginTop: '8px' }}
              >
                {showAdvancedFilters ? 'Скрыть фильтры' : 'Дополнительные фильтры'}
              </Button>
            </div>
          </Card>

          {/* Тест цветов */}
          {showColorTest && <div style={{ flexShrink: 0 }}><ColorTest /></div>}

          {/* Таблица товаров */}
          <Card bodyStyle={{ padding: '8px', display: 'flex', flexDirection: 'column' }} style={{ flex: 1 }}>
            {/* Дополнительные фильтры */}
            <div style={{ 
              marginBottom: '16px', 
              padding: '12px', 
              backgroundColor: '#fafafa', 
              borderRadius: '6px', 
              border: '1px solid #d9d9d9', 
              flexShrink: 0,
              display: 'block'
            }} className={`advanced-filters ${showAdvancedFilters ? 'show' : ''}`}>
              <Row gutter={[16, 8]}>
                <Col xs={24} sm={12} md={6}>
                  <Typography.Text strong style={{ fontSize: '12px', color: '#666' }}>Фильтр по производству:</Typography.Text>
                  <Select
                    style={{ width: '100%', marginTop: '4px' }}
                    placeholder="Все места производства"
                    allowClear
                    size="small"
                    value={productionFilter}
                    onChange={setProductionFilter}
                  >
                    {Array.from(new Set(products.map(p => p.productionLocation?.city).filter(Boolean))).map(city => (
                      <Option key={city} value={city}>{city}</Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Typography.Text strong style={{ fontSize: '12px', color: '#666' }}>Фильтр по выпуску:</Typography.Text>
                  <Select
                    style={{ width: '100%', marginTop: '4px' }}
                    placeholder="Все места выпуска"
                    allowClear
                    size="small"
                    value={releaseFilter}
                    onChange={setReleaseFilter}
                  >
                    {Array.from(new Set(products.map(p => p.releaseLocation?.city).filter(Boolean))).map(city => (
                      <Option key={city} value={city}>{city}</Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Typography.Text strong style={{ fontSize: '12px', color: '#666' }}>Диапазон дат создания:</Typography.Text>
                  <RangePicker
                    style={{ width: '100%', marginTop: '4px' }}
                    placeholder={['От даты', 'До даты']}
                    size="small"
                    value={dateRangeFilter ? [dayjs(dateRangeFilter[0]), dayjs(dateRangeFilter[1])] : null}
                    onChange={(dates) => setDateRangeFilter(dates ? [dates[0].toDate(), dates[1].toDate()] : null)}
                    format="DD.MM.YYYY"
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <div style={{ paddingTop: '20px' }}>
                    <Button 
                      size="small" 
                      onClick={() => {
                        setProductionFilter(null);
                        setReleaseFilter(null);
                        setDateRangeFilter(null);
                      }}
                    >
                      Сбросить фильтры
                    </Button>
                  </div>
                </Col>
              </Row>
            </div>
            
            <div style={{ 
              border: '1px solid #f0f0f0',
              borderRadius: '6px',
              overflow: 'auto'
            }}>
            <Table
              columns={columns}
                dataSource={filteredProducts}
              rowKey="id"
              loading={loading}
              size="small"
                scroll={{ x: 1000 }}
                style={{ width: '100%', tableLayout: 'fixed' }}
              pagination={{
                pageSize: 25,
                  showSizeChanger: false,
                showQuickJumper: true,
                showTotal: (total, range) => 
                    `${range[0]}-${range[1]} из ${total}`
              }}
                sticky
            />
            </div>
          </Card>
        </div>

        {/* Модальное окно изделия */}
        <Modal
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>
                {editingProduct ? 'Редактирование изделия' : 'Новое изделие'}
                {saving && <span style={{ marginLeft: '8px', color: '#1890ff' }}>⏳</span>}
              </span>
            </div>
          }
          open={isModalVisible}
          onCancel={() => {
            if (!saving) {
              setIsModalVisible(false);
              setEditingProduct(null);
            }
          }}
          closable={!saving}
          footer={[
            <Button 
              key="cancel" 
              onClick={() => setIsModalVisible(false)}
              disabled={saving}
            >
              Отмена
            </Button>,
            <Button 
              key="save" 
              type="primary" 
              icon={<SaveOutlined />} 
              onClick={handleSave}
              loading={saving}
              disabled={saving || (editingProduct && hasOwner)}
            >
              {saving ? 'Сохранение...' : 'Сохранить'}
            </Button>
          ]}
          width="95vw"
          style={{ top: 20 }}
          bodyStyle={{ height: '80vh', overflowY: 'auto', padding: '24px' }}
        >
          {/* Сообщение о владельце */}
          {editingProduct && hasOwner && (
            <div style={{
              background: '#fff2e8',
              border: '1px solid #ffb366',
              borderRadius: '6px',
              padding: '12px 16px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center'
            }}>
              <span style={{ fontSize: '16px', marginRight: '8px' }}>⚠️</span>
              <div>
                <div style={{ fontWeight: 'bold', color: '#d46b08', marginBottom: '4px' }}>
                  Товар уже имеет владельца
                </div>
                <div style={{ color: '#8c4a00', fontSize: '14px' }}>
                  Редактирование заблокировано. Товар принадлежит клиенту и не может быть изменен.
                </div>
              </div>
            </div>
          )}

          <Form
            key={forceUpdate}
            form={form}
            layout="vertical"
            disabled={editingProduct && hasOwner}
          >
            {/* Основная информация */}
            <Title level={4}>Основная информация</Title>
            
            {/* Первая строка: Название и фото */}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label="Название изделия"
                  rules={[{ required: true, message: 'Введите название изделия!' }]}
                >
                  <Input placeholder="Например: Jacket Waxed 01" />
                </Form.Item>
                <Form.Item
                  name="status"
                  label="Статус"
                  initialValue="АКТИВНО"
                >
                  <Select placeholder="Выберите статус" size="small">
                    <Option value="АКТИВНО">Активно</Option>
                    <Option value="АННУЛИРОВАНО">Аннулировано</Option>
                    <Option value="АРХИВ">Архив</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Row gutter={8}>
                  <Col span={24}>
                <Form.Item
                      name="photos"
                      label="Фотографии изделия"
                      rules={[{ required: true, message: 'Загрузите хотя бы одно фото изделия!' }]}
                    >
                      <PhotoGallery />
                </Form.Item>
                  </Col>
                </Row>
              </Col>
            </Row>

            {/* Вторая строка: Номера под названием */}
            <Row gutter={16}>
              <Col span={12}>
                <Row gutter={8}>
              <Col span={12}>
                <Form.Item
                  name="serialNumber"
                  label="Серийный номер"
                  rules={[{ required: true, message: 'Введите серийный номер!' }]}
                >
                      <Input placeholder="JW-001-2025" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="identificationNumber"
                  label="Идентификационный номер"
                  rules={[{ required: true, message: 'Введите идентификационный номер!' }]}
                >
                      <Input placeholder="ID-JW-001-2025" />
                </Form.Item>
              </Col>
            </Row>
              </Col>
              <Col span={12}>
                {/* Атрибуты справа */}
                <Row gutter={8}>
              <Col span={8}>
                <Form.Item
                  name="sizeId"
                  label="Размер"
                >
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <Select 
                      key={`size-${forceUpdate}`}
                      placeholder="Размер" 
                      allowClear 
                      size="small" 
                      style={{ flex: 1 }}
                      value={formValues.sizeId}
                      onChange={(value) => {
                        setFormValues(prev => ({ ...prev, sizeId: value }));
                        form.setFieldValue('sizeId', value);
                      }}
                    >
                    {sizes.map(size => (
                      <Option key={size.id} value={size.id}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontWeight: '500' }}>{size.name}</span>
                          {size.description && (
                            <span style={{ fontSize: '12px', color: '#666' }}>
                              ({size.description})
                            </span>
                          )}
                        </div>
                      </Option>
                    ))}
                  </Select>
                    <Tooltip title="Добавить новый размер">
                      <Button 
                        type="text" 
                        icon={<PlusCircleOutlined />} 
                        size="small"
                        onClick={() => setIsAddSizeModalVisible(true)}
                        style={{ padding: '2px 4px' }}
                      />
                    </Tooltip>
                  </div>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="colorId"
                  label="Цвет"
                >
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <Select 
                      key={`color-${forceUpdate}`} 
                      placeholder="Цвет" 
                      allowClear 
                      size="small" 
                      style={{ flex: 1 }}
                      value={formValues.colorId}
                      onChange={(value) => {
                        setFormValues(prev => ({ ...prev, colorId: value }));
                        form.setFieldValue('colorId', value);
                      }}
                    >
                    {colors.map(color => (
                      <Option key={color.id} value={color.id}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {color.hexCode && (
                            <div 
                              style={{ 
                                      width: 12, 
                                      height: 12, 
                                backgroundColor: color.hexCode, 
                                border: '1px solid #ccc',
                                      marginRight: 6,
                                borderRadius: 2
                              }} 
                            />
                          )}
                          {color.name}
                        </div>
                      </Option>
                    ))}
                  </Select>
                    <Tooltip title="Добавить новый цвет">
                      <Button 
                        type="text" 
                        icon={<PlusCircleOutlined />} 
                        size="small"
                        onClick={() => setIsAddColorModalVisible(true)}
                        style={{ padding: '2px 4px' }}
                      />
                    </Tooltip>
                  </div>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="materialId"
                  label="Материал"
                >
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <Select 
                      key={`material-${forceUpdate}`} 
                      placeholder="Материал" 
                      allowClear 
                      size="small" 
                      style={{ flex: 1 }}
                      value={formValues.materialId}
                      onChange={(value) => {
                        setFormValues(prev => ({ ...prev, materialId: value }));
                        form.setFieldValue('materialId', value);
                      }}
                    >
                    {materials.map(material => (
                      <Option key={material.id} value={material.id}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontWeight: '500' }}>{material.name}</span>
                          {material.description && (
                            <span style={{ fontSize: '12px', color: '#666' }}>
                              ({material.description.length > 20 ? material.description.substring(0, 20) + '...' : material.description})
                            </span>
                          )}
                        </div>
                      </Option>
                    ))}
                  </Select>
                    <Tooltip title="Добавить новый материал">
                      <Button 
                        type="text" 
                        icon={<PlusCircleOutlined />} 
                        size="small"
                        onClick={() => setIsAddMaterialModalVisible(true)}
                        style={{ padding: '2px 4px' }}
                      />
                    </Tooltip>
                  </div>
                </Form.Item>
                  </Col>
                </Row>
              </Col>
            </Row>

            {/* Третья строка: Описание и особенности слева, места производства справа */}
            <Row gutter={16}>
              <Col span={12}>
                <Row gutter={8}>
                  <Col span={12}>
                    <Form.Item
                      name="description"
                      label="Описание"
                    >
                      <TextArea rows={2} placeholder="Описание" size="small" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
            <Form.Item
              name="features"
              label="Особенности"
            >
                      <TextArea rows={2} placeholder="Особенности" size="small" />
            </Form.Item>
                  </Col>
                </Row>
              </Col>
              <Col span={12}>
                <Row gutter={8}>
              <Col span={12}>
                <Form.Item
                  name="productionLocationId"
                  label="Место производства"
                >
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <Select 
                      key={`production-${forceUpdate}`} 
                      placeholder="Производство" 
                      allowClear 
                      size="small" 
                      style={{ flex: 1 }}
                      value={formValues.productionLocationId}
                      onChange={(value) => {
                        setFormValues(prev => ({ ...prev, productionLocationId: value }));
                        form.setFieldValue('productionLocationId', value);
                      }}
                    >
                    {productionLocations.map(location => (
                      <Option key={location.id} value={location.id}>
                        {location.fullAddress}
                      </Option>
                    ))}
                  </Select>
                    <Tooltip title="Добавить новое место производства">
                      <Button 
                        type="text" 
                        icon={<PlusCircleOutlined />} 
                        size="small"
                        onClick={() => setIsAddProductionLocationModalVisible(true)}
                        style={{ padding: '2px 4px' }}
                      />
                    </Tooltip>
                  </div>
                </Form.Item>
                    <Form.Item
                      name="releaseDate"
                      label="Дата выпуска"
                      rules={[{ required: true, message: 'Выберите дату выпуска!' }]}
                    >
                      <DatePicker 
                        style={{ width: '100%' }} 
                        size="small" 
                        format="DD.MM.YYYY"
                        placeholder="Выберите дату"
                      />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="releaseLocationId"
                  label="Место выпуска"
                >
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <Select 
                      placeholder="Выпуск" 
                      allowClear 
                      size="small" 
                      style={{ flex: 1 }}
                      value={formValues.releaseLocationId}
                      onChange={(value) => {
                        setFormValues(prev => ({ ...prev, releaseLocationId: value }));
                        form.setFieldValue('releaseLocationId', value);
                      }}
                    >
                    {releaseLocations.map(location => (
                      <Option key={location.id} value={location.id}>
                        {location.fullAddress}
                      </Option>
                    ))}
                  </Select>
                    <Tooltip title="Добавить новое место выпуска">
                      <Button 
                        type="text" 
                        icon={<PlusCircleOutlined />} 
                        size="small"
                        onClick={() => setIsAddReleaseLocationModalVisible(true)}
                        style={{ padding: '2px 4px' }}
                      />
                    </Tooltip>
                  </div>
                </Form.Item>
                <Form.Item
                  name="certificateDate"
                  label="Дата выдачи сертификата"
                >
                      <DatePicker 
                        style={{ width: '100%' }} 
                        size="small" 
                        format="DD.MM.YYYY"
                        placeholder="Выберите дату"
                      />
                </Form.Item>
                  </Col>
                </Row>
              </Col>
            </Row>




          </Form>
        </Modal>

        {/* Модальное окно истории товара */}
        <Modal
          title={`История товара: ${selectedProductForHistory?.name || ''}`}
          open={isHistoryModalVisible}
          onCancel={() => setIsHistoryModalVisible(false)}
          footer={null}
          width={800}
        >
          <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
            {historyLoading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Text>Загрузка истории...</Text>
              </div>
            ) : productHistory.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Text type="secondary">История товара пуста</Text>
              </div>
            ) : (
              <div>
                {productHistory.map((item, index) => (
                  <Card 
                    key={index} 
                    size="small" 
                    style={{ marginBottom: '8px' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                          {item.actionType === 'created' && 'Создание товара'}
                          {item.actionType === 'updated' && 'Обновление товара'}
                          {item.actionType === 'transferred' && 'Передача товара'}
                          {item.actionType === 'sold' && 'Продажа товара'}
                          {item.actionType === 'received' && 'Получение товара'}
                          {item.actionType === 'status_changed' && 'Изменение статуса'}
                          {item.actionType === 'photo_updated' && 'Обновление фото'}
                          {item.actionType === 'deleted' && 'Удаление товара'}
                        </div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {item.description}
                        </Text>
                      </div>
                      <div style={{ textAlign: 'right', minWidth: '120px' }}>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {item.actionDate || item.action_date ? 
                            dayjs(item.actionDate || item.action_date).format('DD.MM.YYYY HH:mm') : 
                            'Дата не указана'}
                        </div>
                        {item.user && (
                          <div style={{ fontSize: '11px', color: '#999' }}>
                            {item.user.firstName} {item.user.lastName}
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </Modal>

        {/* Модальное окно владельцев товара */}
        <Modal
          title={`Владельцы товара: ${selectedProductForHistory?.name || ''}`}
          open={isOwnersModalVisible}
          onCancel={() => setIsOwnersModalVisible(false)}
          footer={null}
          width={600}
        >
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {ownersLoading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Text>Загрузка владельцев...</Text>
              </div>
            ) : productOwners.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Text type="secondary">Нет информации о владельцах</Text>
              </div>
            ) : (
              <div>
                {productOwners.map((owner, index) => (
                  <Card 
                    key={index} 
                    size="small" 
                    style={{ 
                      marginBottom: '8px',
                      border: owner.status === 'current' ? '2px solid #52c41a' : '1px solid #d9d9d9'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                          <Text strong>
                            {owner.client?.fullName || 
                             `${owner.client?.firstName || ''} ${owner.client?.lastName || ''}`.trim() || 
                             'Неизвестный владелец'}
                          </Text>
                          {owner.status === 'current' && (
                            <Tag color="green" style={{ marginLeft: '8px', fontSize: '10px' }}>
                              ТЕКУЩИЙ
                            </Tag>
                          )}
                          {owner.status === 'previous' && (
                            <Tag color="default" style={{ marginLeft: '8px', fontSize: '10px' }}>
                              ПРЕДЫДУЩИЙ
                            </Tag>
                          )}
                        </div>
                        {owner.client?.email && (
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {owner.client.email}
                          </div>
                        )}
                        {owner.client?.phone && (
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            Телефон: {owner.client.phone}
                          </div>
                        )}
                        {owner.transferReason && (
                          <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                            Причина: {owner.transferReason}
                          </div>
                        )}
                      </div>
                      <div style={{ textAlign: 'right', minWidth: '120px' }}>
                        {owner.ownershipStartDate ? (
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            С {dayjs(owner.ownershipStartDate).format('DD.MM.YYYY')}
                          </div>
                        ) : (
                          <div style={{ fontSize: '12px', color: '#999' }}>
                            Дата не указана
                          </div>
                        )}
                        {owner.ownershipEndDate ? (
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            По {dayjs(owner.ownershipEndDate).format('DD.MM.YYYY')}
                          </div>
                        ) : owner.status === 'current' && (
                          <div style={{ fontSize: '12px', color: '#52c41a' }}>
                            Текущий
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </Modal>

        {/* Модальные окна для добавления элементов справочников */}
        
        {/* Добавление размера */}
        <Modal
          title="Добавить новый размер"
          open={isAddSizeModalVisible}
          onCancel={() => {
            setIsAddSizeModalVisible(false);
            if (wasReferenceModalOpen) {
              setIsReferenceModalVisible(true);
              setWasReferenceModalOpen(false);
            }
          }}
          footer={null}
          width={400}
        >
          <Form
            form={newItemForm}
            layout="vertical"
            onFinish={handleAddSize}
          >
            <Form.Item
              name="name"
              label="Название размера"
              rules={[{ required: true, message: 'Введите название размера!' }]}
            >
              <Input placeholder="Например: XL" />
            </Form.Item>
            <Form.Item
              name="description"
              label="Описание размера"
            >
              <Input placeholder="Например: Очень большой размер" />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={addLoading}>
                  Добавить
                </Button>
                <Button onClick={() => {
                  setIsAddSizeModalVisible(false);
                  if (wasReferenceModalOpen) {
                    setIsReferenceModalVisible(true);
                    setWasReferenceModalOpen(false);
                  }
                }}>
                  Отмена
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Добавление цвета */}
        <Modal
          title="Добавить новый цвет"
          open={isAddColorModalVisible}
          onCancel={() => {
            setIsAddColorModalVisible(false);
            setSelectedColor('#000000');
            if (wasReferenceModalOpen) {
              setIsReferenceModalVisible(true);
              setWasReferenceModalOpen(false);
            }
          }}
          footer={null}
          width={400}
        >
          <Form
            form={newItemForm}
            layout="vertical"
            onFinish={handleAddColor}
          >
            <Form.Item
              name="name"
              label="Название цвета"
              rules={[{ required: true, message: 'Введите название цвета!' }]}
            >
              <Input placeholder="Например: Темно-синий" />
            </Form.Item>
            <Form.Item
              name="hexCode"
              label="Цветовой код (HEX)"
              rules={[{ pattern: /^#[0-9A-F]{6}$/i, message: 'Введите корректный HEX код!' }]}
            >
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <Input 
                  placeholder="#000000" 
                  style={{ flex: 1 }}
                  value={selectedColor}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedColor(value);
                    newItemForm.setFieldsValue({ hexCode: value });
                  }}
                />
                <input 
                  type="color" 
                  value={selectedColor}
                  onChange={(e) => {
                    const hexValue = e.target.value;
                    setSelectedColor(hexValue);
                    newItemForm.setFieldsValue({ hexCode: hexValue });
                  }}
                  style={{ 
                    width: '40px', 
                    height: '32px', 
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                />
              </div>
            </Form.Item>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={addLoading}>
                  Добавить
                </Button>
                <Button onClick={() => {
                  setIsAddColorModalVisible(false);
                  setSelectedColor('#000000');
                  if (wasReferenceModalOpen) {
                    setIsReferenceModalVisible(true);
                    setWasReferenceModalOpen(false);
                  }
                }}>
                  Отмена
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Добавление материала */}
        <Modal
          title="Добавить новый материал"
          open={isAddMaterialModalVisible}
          onCancel={() => {
            setIsAddMaterialModalVisible(false);
            if (wasReferenceModalOpen) {
              setIsReferenceModalVisible(true);
              setWasReferenceModalOpen(false);
            }
          }}
          footer={null}
          width={400}
        >
          <Form
            form={newItemForm}
            layout="vertical"
            onFinish={handleAddMaterial}
          >
            <Form.Item
              name="name"
              label="Название материала"
              rules={[{ required: true, message: 'Введите название материала!' }]}
            >
              <Input placeholder="Например: Деним" />
            </Form.Item>
            <Form.Item
              name="description"
              label="Описание материала"
            >
              <Input placeholder="Например: Прочная хлопчатобумажная ткань" />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={addLoading}>
                  Добавить
                </Button>
                <Button onClick={() => {
                  setIsAddMaterialModalVisible(false);
                  if (wasReferenceModalOpen) {
                    setIsReferenceModalVisible(true);
                    setWasReferenceModalOpen(false);
                  }
                }}>
                  Отмена
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Добавление места производства */}
        <Modal
          title="Добавить новое место производства"
          open={isAddProductionLocationModalVisible}
          destroyOnClose
          onCancel={() => {
            setIsAddProductionLocationModalVisible(false);
            newItemForm.resetFields();
            if (wasReferenceModalOpen) {
              setIsReferenceModalVisible(true);
              setWasReferenceModalOpen(false);
            }
          }}
          footer={null}
          width={500}
        >
          <Form
            form={newItemForm}
            layout="vertical"
            onFinish={handleAddProductionLocation}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="country"
                  label="Страна"
                  rules={[{ required: true, message: 'Введите страну!' }]}
                >
                  <Input placeholder="Российская Федерация" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="region"
                  label="Регион/Область"
                >
                  <Input placeholder="Ленинградская область" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
            <Form.Item
                  name="city"
                  label="Город"
                  rules={[{ required: true, message: 'Введите город!' }]}
                >
                  <Input placeholder="Санкт-Петербург" />
            </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="fullAddress"
                  label="Полный адрес"
                  rules={[{ required: true, message: 'Введите полный адрес!' }]}
                >
                  <Input placeholder="Российская Федерация, г. Санкт-Петербург" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={addLoading}>
                  Добавить
                </Button>
                <Button onClick={() => {
                  setIsAddProductionLocationModalVisible(false);
                  if (wasReferenceModalOpen) {
                    setIsReferenceModalVisible(true);
                    setWasReferenceModalOpen(false);
                  }
                }}>
                  Отмена
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Добавление места выпуска */}
        <Modal
          title="Добавить новое место выпуска"
          open={isAddReleaseLocationModalVisible}
          destroyOnClose
          onCancel={() => {
            setIsAddReleaseLocationModalVisible(false);
            newItemForm.resetFields();
            if (wasReferenceModalOpen) {
              setIsReferenceModalVisible(true);
              setWasReferenceModalOpen(false);
            }
          }}
          footer={null}
          width={500}
        >
          <Form
            form={newItemForm}
            layout="vertical"
            onFinish={handleAddReleaseLocation}
          >
            <Row gutter={16}>
              <Col span={12}>
            <Form.Item
                  name="country"
                  label="Страна"
                  rules={[{ required: true, message: 'Введите страну!' }]}
                >
                  <Input placeholder="Российская Федерация" />
            </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="region"
                  label="Регион/Область"
                >
                  <Input placeholder="Иркутская область" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="city"
                  label="Город"
                  rules={[{ required: true, message: 'Введите город!' }]}
                >
                  <Input placeholder="Братск" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="fullAddress"
                  label="Полный адрес"
                  rules={[{ required: true, message: 'Введите полный адрес!' }]}
                >
                  <Input placeholder="Российская Федерация, Иркутская область, г. Братск" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={addLoading}>
                  Добавить
                </Button>
                <Button onClick={() => {
                  setIsAddReleaseLocationModalVisible(false);
                  if (wasReferenceModalOpen) {
                    setIsReferenceModalVisible(true);
                    setWasReferenceModalOpen(false);
                  }
                }}>
                  Отмена
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Модальное окно справочников */}
        <Modal
          title="Справочники"
          open={isReferenceModalVisible}
          onCancel={() => setIsReferenceModalVisible(false)}
          footer={null}
          width={800}
        >
          <Tabs 
            defaultActiveKey="1"
            items={[
              {
                key: '1',
                label: 'РАЗМЕР',
                children: (
                  <>
                    <div style={{ marginBottom: '16px' }}>
                      <Button 
                        type="primary" 
                        icon={<PlusOutlined />}
                        onClick={() => {
                          setWasReferenceModalOpen(true);
                          setIsAddSizeModalVisible(true);
                        }}
                      >
                        Добавить размер
                      </Button>
                    </div>
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {sizes.map(size => (
                        <Card key={size.id} size="small" style={{ marginBottom: '8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <Text strong>{size.name}</Text>
                              {size.description && (
                                <div>
                                  <Text type="secondary" style={{ fontSize: '12px' }}>{size.description}</Text>
                                </div>
                              )}
                            </div>
                            <Popconfirm
                              title="Удалить размер?"
                              description={`Вы уверены, что хотите удалить размер "${size.name}"?`}
                              onConfirm={() => handleDeleteSize(size.id)}
                              okText="Да"
                              cancelText="Нет"
                            >
                              <Button 
                                type="text" 
                                danger 
                                icon={<DeleteOutlined />} 
                                size="small"
                              />
                            </Popconfirm>
                          </div>
                        </Card>
                      ))}
                      {sizes.length === 0 && (
                        <Text type="secondary">Нет размеров</Text>
                      )}
                    </div>
                  </>
                )
              },
              {
                key: '2',
                label: 'ЦВЕТ',
                children: (
                  <>
                    <div style={{ marginBottom: '16px' }}>
                      <Button 
                        type="primary" 
                        icon={<PlusOutlined />}
                        onClick={() => {
                          setWasReferenceModalOpen(true);
                          setIsAddColorModalVisible(true);
                        }}
                      >
                        Добавить цвет
                      </Button>
                    </div>
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {colors.map(color => (
                        <Card key={color.id} size="small" style={{ marginBottom: '8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              {color.hexCode && (
                                <div 
                                  style={{ 
                                    width: '24px', 
                                    height: '24px', 
                                    backgroundColor: color.hexCode, 
                                    border: '1px solid #d9d9d9',
                                    borderRadius: '4px'
                                  }} 
                                />
                              )}
                              <div>
                                <Text strong>{color.name}</Text>
                                {color.hexCode && (
                                  <div>
                                    <Text type="secondary" style={{ fontSize: '12px' }}>{color.hexCode}</Text>
                                  </div>
                                )}
                              </div>
                            </div>
                            <Popconfirm
                              title="Удалить цвет?"
                              description={`Вы уверены, что хотите удалить цвет "${color.name}"?`}
                              onConfirm={() => handleDeleteColor(color.id)}
                              okText="Да"
                              cancelText="Нет"
                            >
                              <Button 
                                type="text" 
                                danger 
                                icon={<DeleteOutlined />} 
                                size="small"
                              />
                            </Popconfirm>
                          </div>
                        </Card>
                      ))}
                      {colors.length === 0 && (
                        <Text type="secondary">Нет цветов</Text>
                      )}
                    </div>
                  </>
                )
              },
              {
                key: '3',
                label: 'Материал',
                children: (
                  <>
                    <div style={{ marginBottom: '16px' }}>
                      <Button 
                        type="primary" 
                        icon={<PlusOutlined />}
                        onClick={() => {
                          setWasReferenceModalOpen(true);
                          setIsAddMaterialModalVisible(true);
                        }}
                      >
                        Добавить материал
                      </Button>
                    </div>
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {materials.map(material => (
                        <Card key={material.id} size="small" style={{ marginBottom: '8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <Text strong>{material.name}</Text>
                              {material.description && (
                                <div>
                                  <Text type="secondary" style={{ fontSize: '12px' }}>{material.description}</Text>
                                </div>
                              )}
                              {material.category && (
                                <div>
                                  <Tag color="blue">{material.category}</Tag>
                                </div>
                              )}
                            </div>
                            <Popconfirm
                              title="Удалить материал?"
                              description={`Вы уверены, что хотите удалить материал "${material.name}"?`}
                              onConfirm={() => handleDeleteMaterial(material.id)}
                              okText="Да"
                              cancelText="Нет"
                            >
                              <Button 
                                type="text" 
                                danger 
                                icon={<DeleteOutlined />} 
                                size="small"
                              />
                            </Popconfirm>
                          </div>
                        </Card>
                      ))}
                      {materials.length === 0 && (
                        <Text type="secondary">Нет материалов</Text>
                      )}
                    </div>
                  </>
                )
              },
              {
                key: '4',
                label: 'Место производства',
                children: (
                  <>
                    <div style={{ marginBottom: '16px' }}>
                      <Button 
                        type="primary" 
                        icon={<PlusOutlined />}
                        onClick={() => {
                          setWasReferenceModalOpen(true);
                          setIsAddProductionLocationModalVisible(true);
                        }}
                      >
                        Добавить место производства
                      </Button>
                    </div>
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {productionLocations.map(location => (
                        <Card key={location.id} size="small" style={{ marginBottom: '8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <Text strong>{location.fullAddress || `${location.country}, ${location.city}`}</Text>
                              <div>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  {location.country}
                                  {location.region && `, ${location.region}`}
                                  {location.city && `, ${location.city}`}
                                </Text>
                              </div>
                            </div>
                            <Popconfirm
                              title="Удалить место производства?"
                              description={`Вы уверены, что хотите удалить "${location.fullAddress || `${location.country}, ${location.city}`}"?`}
                              onConfirm={() => handleDeleteProductionLocation(location.id)}
                              okText="Да"
                              cancelText="Нет"
                            >
                              <Button 
                                type="text" 
                                danger 
                                icon={<DeleteOutlined />} 
                                size="small"
                              />
                            </Popconfirm>
                          </div>
                        </Card>
                      ))}
                      {productionLocations.length === 0 && (
                        <Text type="secondary">Нет мест производства</Text>
                      )}
                    </div>
                  </>
                )
              },
              {
                key: '5',
                label: 'Место Выпуска',
                children: (
                  <>
                    <div style={{ marginBottom: '16px' }}>
                      <Button 
                        type="primary" 
                        icon={<PlusOutlined />}
                        onClick={() => {
                          setWasReferenceModalOpen(true);
                          setIsAddReleaseLocationModalVisible(true);
                        }}
                      >
                        Добавить место выпуска
                      </Button>
                    </div>
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {releaseLocations.map(location => (
                        <Card key={location.id} size="small" style={{ marginBottom: '8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <Text strong>{location.fullAddress || `${location.country}, ${location.city}`}</Text>
                              <div>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  {location.country}
                                  {location.region && `, ${location.region}`}
                                  {location.city && `, ${location.city}`}
                                </Text>
                              </div>
                            </div>
                            <Popconfirm
                              title="Удалить место выпуска?"
                              description={`Вы уверены, что хотите удалить "${location.fullAddress || `${location.country}, ${location.city}`}"?`}
                              onConfirm={() => handleDeleteReleaseLocation(location.id)}
                              okText="Да"
                              cancelText="Нет"
                            >
                              <Button 
                                type="text" 
                                danger 
                                icon={<DeleteOutlined />} 
                                size="small"
                              />
                            </Popconfirm>
                          </div>
                        </Card>
                      ))}
                      {releaseLocations.length === 0 && (
                        <Text type="secondary">Нет мест выпуска</Text>
                      )}
                    </div>
                  </>
                )
              }
            ]}
          />
        </Modal>

        {/* Модальное окно импорта товаров */}
        <Modal
          title={showImportReport ? "Отчет об импорте товаров" : "Сопоставление полей CSV"}
          open={isImportModalVisible}
          onCancel={() => {
            setIsImportModalVisible(false);
            setShowImportReport(false);
            setImportResults(null);
          }}
          width={showImportReport ? 500 : 800}
          footer={showImportReport ? [
            <Button 
              key="close" 
              type="primary"
              onClick={() => {
                setIsImportModalVisible(false);
                setShowImportReport(false);
                setImportResults(null);
              }}
            >
              Закрыть
            </Button>
          ] : [
            <Button key="cancel" onClick={() => setIsImportModalVisible(false)}>
              Отмена
            </Button>,
            <Button 
              key="import" 
              type="primary" 
              loading={importLoading}
              onClick={handleFinalImport}
              disabled={!fieldMapping || Object.keys(fieldMapping).length === 0 || !Object.values(fieldMapping).includes('название') || !Object.values(fieldMapping).includes('идентификационный номер')}
            >
              Импортировать
            </Button>
          ]}
        >
          {showImportReport ? (
            // Содержимое отчета
            <div style={{ maxHeight: '400px', overflowY: 'auto', maxWidth: '100%', wordWrap: 'break-word' }}>
              <div style={{ marginBottom: '16px' }}>
                <strong>Результат:</strong> Успешно создано {importResults?.success || 0}, Ошибок {importResults?.errors || 0}
              </div>
              
              {importResults?.details && importResults.details.length > 0 && (
                <>
                  {importResults.details.filter(d => d.success).length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <strong style={{ color: '#52c41a' }}>✅ Созданные товары:</strong>
                      <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                        {importResults.details.filter(d => d.success).map((item, index) => (
                          <li key={index} style={{ marginBottom: '8px' }}>
                            <div>
                              <strong>{item.product['название']}</strong> (ID: {item.productId})
                            </div>
                            {item.photoCopyInfo && (
                              <div style={{ fontSize: '12px', color: item.photoCopyInfo.success ? '#52c41a' : '#faad14', marginLeft: '10px' }}>
                                📷 {item.photoCopyInfo.message}
                                {item.photoCopyInfo.success && item.photoCopyInfo.newUrl && (
                                  <div style={{ fontSize: '11px', color: '#666', marginTop: '2px', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    Новый URL: {item.photoCopyInfo.newUrl.substring(0, 50)}...
              </div>
            )}
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {importResults.details.filter(d => !d.success).length > 0 && (
                    <div>
                      <strong style={{ color: '#ff4d4f' }}>❌ Ошибки:</strong>
                      <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                        {importResults.details.filter(d => !d.success).map((item, index) => (
                          <li key={index} style={{ marginBottom: '4px', color: '#ff4d4f' }}>
                            <strong>{item.product['название']}:</strong> {item.error}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            // Содержимое импорта
            <>
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f0f8ff', border: '1px solid #1890ff', borderRadius: '4px' }}>
              <Typography.Text strong style={{ color: '#1890ff' }}>ℹ️ Обработка URL фотографий:</Typography.Text>
              <ul style={{ marginTop: '8px', marginBottom: '0', paddingLeft: '20px' }}>
                <li><strong>Яндекс Диск:</strong> Ссылки вида <code>https://disk.yandex.ru/...</code> скачиваются и копируются в наше хранилище</li>
                <li><strong>Наш S3:</strong> Ссылки вида <code>https://s3.twcstorage.ru/...</code> скачиваются и создаются уникальные копии</li>
                <li><strong>Другие URL:</strong> Любые прямые ссылки на изображения также копируются в наше хранилище</li>
                <li><strong>Уникальность:</strong> Каждая картинка создается как уникальная копия, даже если исходные одинаковые</li>
                <li><strong>Проверки:</strong> Максимальный размер файла 10MB, поддерживаются только изображения</li>
                <li><strong>При ошибке:</strong> Сохраняется оригинальная ссылка с предупреждением</li>
              </ul>
              </div>
          <div style={{ marginBottom: 16 }}>
            <Typography.Text strong>Сопоставьте столбцы CSV с полями системы:</Typography.Text>
              </div>
          
          {/* Предпросмотр данных */}
          <div style={{ marginBottom: 16 }}>
            <Typography.Text strong>Предпросмотр данных:</Typography.Text>
            <div style={{ 
              border: '1px solid #d9d9d9', 
              borderRadius: '4px', 
              padding: '8px', 
              marginTop: '8px',
              maxHeight: '200px',
              overflow: 'auto'
            }}>
              {csvHeaders.length > 0 && (
                <table style={{ width: '100%', fontSize: '12px' }}>
                  <thead>
                    <tr>
                      {csvHeaders.map(header => (
                        <th key={header} style={{ border: '1px solid #d9d9d9', padding: '4px', backgroundColor: '#f5f5f5' }}>
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {csvData.map((row, index) => (
                      <tr key={index}>
                        {csvHeaders.map(header => (
                          <td key={header} style={{ border: '1px solid #d9d9d9', padding: '4px' }}>
                            {row[header] || '-'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Сопоставление полей */}
          <div>
            <Typography.Text strong>Сопоставление полей:</Typography.Text>
            <div style={{ marginTop: '8px' }}>
              {csvHeaders.filter(header => 
                !['id', 'ID', 'Id'].includes(header.trim())
              ).map(csvHeader => (
                <div key={csvHeader} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  marginBottom: '8px',
                  padding: '8px',
                  border: '1px solid #f0f0f0',
                  borderRadius: '4px'
                }}>
                  <div style={{ width: '200px', fontWeight: '500' }}>
                    {csvHeader}
                  </div>
                  <div style={{ margin: '0 16px' }}>→</div>
                  <Select
                    style={{ width: '250px' }}
                    placeholder="Выберите поле системы"
                    value={fieldMapping[csvHeader]}
                    onChange={(value) => {
                      setFieldMapping(prev => ({
                        ...prev,
                        [csvHeader]: value
                      }));
                    }}
                    allowClear
                  >
                    {systemFields.map(field => (
                      <Option key={field.key} value={field.key}>
                        {field.label} {field.required && <span style={{ color: 'red' }}>*</span>}
                      </Option>
                    ))}
                  </Select>
                </div>
              ))}
            </div>
          </div>

          <div style={{ 
            marginTop: '16px', 
            padding: '12px', 
            backgroundColor: (!fieldMapping || Object.keys(fieldMapping).length === 0 || !Object.values(fieldMapping).includes('название') || !Object.values(fieldMapping).includes('идентификационный номер')) ? '#fff2e8' : '#f6ffed',
            border: `1px solid ${(!fieldMapping || Object.keys(fieldMapping).length === 0 || !Object.values(fieldMapping).includes('название') || !Object.values(fieldMapping).includes('идентификационный номер')) ? '#ffbb96' : '#b7eb8f'}`,
            borderRadius: '4px' 
          }}>
            <Typography.Text type={(!fieldMapping || Object.keys(fieldMapping).length === 0 || !Object.values(fieldMapping).includes('название') || !Object.values(fieldMapping).includes('идентификационный номер')) ? 'warning' : 'success'}>
              <strong>Обязательные поля:</strong> 
              {(!fieldMapping || !Object.values(fieldMapping).includes('название')) && <span style={{ color: 'red' }}> Название товара</span>}
              {(!fieldMapping || (!Object.values(fieldMapping).includes('название') && !Object.values(fieldMapping).includes('идентификационный номер'))) && ' и'}
              {(!fieldMapping || !Object.values(fieldMapping).includes('идентификационный номер')) && <span style={{ color: 'red' }}> Идентификационный номер</span>}
              {(!fieldMapping || Object.keys(fieldMapping).length === 0 || !Object.values(fieldMapping).includes('название') || !Object.values(fieldMapping).includes('идентификационный номер')) 
                ? ' должны быть сопоставлены для импорта.' 
                : ' сопоставлены ✓'}
            </Typography.Text>
          </div>
          </>
          )}
        </Modal>

        {/* Модальное окно загрузки фото из S3 */}
        <Modal
          title="Загрузка фото из S3"
          open={isS3ModalVisible}
          onCancel={() => {
            setIsS3ModalVisible(false);
            setSelectedFiles([]);
          }}
          width={800}
          footer={[
            <Button key="close" onClick={() => setIsS3ModalVisible(false)}>
              Закрыть
            </Button>
          ]}
        >
          <div style={{ marginBottom: 16 }}>
            <Upload
              multiple
              accept="image/*"
              showUploadList={false}
              beforeUpload={(file, fileList) => {
                // fileList содержит все выбранные файлы
                handleBulkUpload(fileList);
                return false;
              }}
            >
              <Button 
                icon={<CloudUploadOutlined />} 
                loading={s3Loading}
                style={{ marginBottom: 16 }}
              >
                Загрузить файлы (с префиксом load_)
              </Button>
            </Upload>
          </div>

          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            {s3Loading ? (
              <div style={{ textAlign: 'center', padding: 20 }}>
                Загрузка файлов...
              </div>
            ) : (
              <div style={{ border: '1px solid #d9d9d9', borderRadius: 8 }}>
                {/* Заголовок таблицы */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '120px 1fr 40px 40px 120px',
                  gap: 16,
                  padding: '12px 16px',
                  backgroundColor: '#f5f5f5',
                  borderBottom: '1px solid #d9d9d9',
                  fontWeight: 'bold',
                  fontSize: 14
                }}>
                  <div>Дата загрузки</div>
                  <div>Имя файла</div>
                  <div>Просмотр</div>
                  <div>Удалить</div>
                  <div>Копировать</div>
                </div>

                {/* Строки файлов */}
                {s3Files.map((file, index) => (
                  <div key={index} style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '120px 1fr 40px 40px 120px',
                    gap: 16,
                    padding: '12px 16px',
                    borderBottom: index < s3Files.length - 1 ? '1px solid #f0f0f0' : 'none',
                    alignItems: 'center'
                  }}>
                    {/* Дата загрузки */}
                    <div style={{ fontSize: 12, color: '#666' }}>
                      {new Date(file.lastModified).toLocaleDateString('ru-RU', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>

                    {/* Имя файла */}
                    <div style={{ fontSize: 14, wordBreak: 'break-all' }}>
                      {file.name}
                    </div>

                    {/* Иконка просмотра */}
                    <div style={{ textAlign: 'center' }}>
                      <Button
                        type="text"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => showImagePreview(file.url, file.name)}
                        title="Посмотреть изображение"
                      />
                    </div>

                    {/* Иконка удаления */}
                    <div style={{ textAlign: 'center' }}>
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          Modal.confirm({
                            title: 'Удалить файл?',
                            content: `Вы уверены, что хотите удалить файл "${file.name}"?`,
                            okText: 'Удалить',
                            okType: 'danger',
                            cancelText: 'Отмена',
                            onOk() {
                              deleteS3File(file.name);
                            }
                          });
                        }}
                        title="Удалить файл"
                      />
                    </div>

                    {/* Кнопка копирования */}
                    <div>
                      <Button
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => copyFileLink(file.url)}
                        style={{ width: '100%' }}
                      >
                        Копировать
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Modal>

        {/* Модальное окно для просмотра изображений */}
        <Modal
          title={`Просмотр изображения: ${previewImageName}`}
          open={imagePreviewVisible}
          onCancel={() => setImagePreviewVisible(false)}
          footer={[
            <Button key="close" onClick={() => setImagePreviewVisible(false)}>
              Закрыть
            </Button>
          ]}
          width="auto"
          style={{ maxWidth: '90vw' }}
        >
          <div style={{ textAlign: 'center' }}>
            <img
              src={previewImageUrl}
              alt={previewImageName}
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <div style={{ 
              display: 'none',
              padding: 20,
              color: '#999'
            }}>
              Ошибка загрузки изображения
            </div>
          </div>
        </Modal>
      </Content>
    </Layout>
    </ConfigProvider>
  );
};

export default ProductsNew;
